import { useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { AdminRoute, ProtectedRoute } from "./components/ProtectedRoute";
import { useAuth } from "./auth/AuthContext";
import { FullscreenLoader } from "./components/Loaders";
import { SeoManager } from "./seo";
import { trackPageView } from "./analytics";

import { Home } from "./pages/Home";
import { Dashboard } from "./pages/Dashboard";
import { Pricing } from "./pages/Pricing";
import { About } from "./pages/About";
import { Downloads } from "./pages/Downloads";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Account } from "./pages/Account";
import { Build } from "./pages/Build";
import { Bloomy } from "./pages/Bloomy";
import { Analyze } from "./pages/Analyze";
import { Collection } from "./pages/Collection";
import { SavedDeckDetailsPage, SavedDecks } from "./pages/SavedDecks";
import { PublicDeckDetailsPage, PublicDecks } from "./pages/PublicDecks";
import { AdminLayout } from "./pages/admin/AdminLayout";
import { AdminOverview } from "./pages/admin/AdminOverview";
import { AdminLlm } from "./pages/admin/AdminLlm";
import { AdminUsers } from "./pages/admin/AdminUsers";
import { AdminUpdate } from "./pages/admin/AdminUpdate";
import { NotFound } from "./pages/NotFound";

function HomeOrDashboard() {
  const { firebaseUser, loading } = useAuth();
  if (loading) return <FullscreenLoader label="Loading..." />;
  return firebaseUser ? <Dashboard /> : <Home />;
}

function ScrollToTop() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      const el = document.getElementById(id);
      el?.scrollIntoView();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search, hash]);

  return null;
}

function AnalyticsPageView() {
  const { pathname, search } = useLocation();
  const isFirstPath = useRef(true);

  useEffect(() => {
    const path = `${pathname}${search}`;
    if (isFirstPath.current) {
      isFirstPath.current = false;
      // First page view is sent by gtag("config", ...) in index.html.
      return;
    }
    trackPageView(path);
  }, [pathname, search]);

  return null;
}

export function App() {
  return (
    <>
      <SeoManager />
      <ScrollToTop />
      <AnalyticsPageView />
      <Navbar />
      <Routes>
        <Route path="/" element={<HomeOrDashboard />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/downloads" element={<Downloads />} />
        <Route path="/community" element={<PublicDecks />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        <Route
          path="/build"
          element={
            <ProtectedRoute>
              <Build />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bloomy"
          element={
            <ProtectedRoute>
              <Bloomy />
            </ProtectedRoute>
          }
        />
        <Route path="/app" element={<Navigate to="/" replace />} />
        <Route
          path="/analyze"
          element={
            <ProtectedRoute>
              <Analyze />
            </ProtectedRoute>
          }
        />
        <Route
          path="/collection"
          element={
            <ProtectedRoute>
              <Collection />
            </ProtectedRoute>
          }
        />
        <Route
          path="/decks"
          element={
            <ProtectedRoute>
              <SavedDecks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/decks/:id"
          element={
            <ProtectedRoute>
              <SavedDeckDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/community/:id" element={<PublicDeckDetailsPage />} />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminOverview />} />
          <Route path="llm" element={<AdminLlm />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="update" element={<AdminUpdate />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
    </>
  );
}
